export type RequestStatus = 'recue' | 'en-traitement' | 'resolue'

export interface TimelineStep {
  date: string
  label: string
}

export interface RequestAttachment {
  name: string
  /** Drives the icon shown next to the file. */
  type: 'image' | 'pdf' | 'form'
  size: string
}

export type MessageAuthor = 'citoyen' | 'ville'

export interface RequestMessage {
  id: string
  author: MessageAuthor
  authorName: string
  sentAt: string
  body: string
  attachment?: RequestAttachment
  /** Set when the Ville expects something back from the citizen. */
  awaiting?: 'document' | 'info'
}

export interface CitizenRequest {
  id: string
  reference: string
  title: string
  /** Service municipal responsable (ex. Travaux publics). */
  service: string
  /** Sous-catégorie de la requête (ex. Collecte des déchets). */
  category: string
  address: string
  fullAddress: string
  submittedOn: string
  createdOn: string
  resolvedOn: string | null
  createdBy: string
  status: RequestStatus
  description: string
  attachments: RequestAttachment[]
  timeline: TimelineStep[]
  messages: RequestMessage[]
  response?: string
}

export const statusLabels: Record<RequestStatus, string> = {
  recue: 'Reçue',
  'en-traitement': 'En traitement',
  resolue: 'Résolue',
}

export const statusOrder: RequestStatus[] = ['recue', 'en-traitement', 'resolue']

export const quickReports = ['Route / nid-de-poule', 'Éclairage', 'Arbre', 'Eau', 'Collectes', 'Autre']

export const profileAddress = '4590, rue Bourgogne'

/** Very small keyword classifier used to simulate ATNA's understanding. */
export const intents = [
  {
    keywords: ['nid-de-poule', 'nid de poule', 'trou', 'asphalte', 'route', 'chaussée'],
    label: 'Nid-de-poule',
    service: 'Voirie',
    question: 'Depuis quand avez-vous remarqué le problème ?',
    answers: ['Aujourd’hui', 'Cette semaine', 'Depuis plus d’un mois'],
    photoHint: true,
  },
  {
    keywords: ['lampadaire', 'lumière', 'éclairage', 'ampoule'],
    label: 'Lampadaire défectueux',
    service: 'Éclairage public',
    question: 'Le lampadaire est-il complètement éteint ou clignote-t-il ?',
    answers: ['Complètement éteint', 'Il clignote', 'Il reste allumé le jour'],
    photoHint: false,
  },
  {
    keywords: ['arbre', 'branche', 'tombé'],
    label: 'Arbre ou branche à dégager',
    service: 'Parcs et espaces verts',
    question: 'L’arbre bloque-t-il la circulation ?',
    answers: ['Oui, la rue est bloquée', 'Non, sur le trottoir', 'Non, sur un terrain'],
    photoHint: true,
  },
  {
    keywords: ['eau', 'fuite', 'coule', 'aqueduc', 'trottoir mouillé'],
    label: 'Fuite d’eau',
    service: 'Aqueduc',
    question: 'L’écoulement est-il continu ?',
    answers: ['Oui, en continu', 'Par intermittence', 'Je ne sais pas'],
    photoHint: true,
  },
  {
    keywords: ['bac', 'collecte', 'ordures', 'recyclage', 'ramassé'],
    label: 'Bac non ramassé',
    service: 'Collectes',
    question: 'Quel bac n’a pas été ramassé ?',
    answers: ['Recyclage', 'Ordures', 'Matières organiques'],
    photoHint: false,
  },
]

export const emergencyKeywords = [
  'fil électrique',
  'fil electrique',
  'électrocut',
  'feu',
  'incendie',
  'gaz',
  'blessé',
  'accident',
  'fumée',
]

export const requests: CitizenRequest[] = [
  {
    id: 'req-2903',
    reference: '#REQ-2903',
    title: 'Animal trouvé',
    service: 'Service animalier',
    category: 'Animal errant ou trouvé',
    address: 'Parc Maupassant, Brossard',
    fullAddress: '7000, boulevard Maupassant, Brossard, Québec, J4Z 1R5',
    submittedOn: 'Envoyée aujourd’hui',
    createdOn: '03/09/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'recue',
    description:
      'Chien trouvé sans médaille lisible près de l’aire de jeux du parc. L’animal est calme et gardé en laisse en attendant une prise en charge.',
    attachments: [{ name: 'chien-trouve-parc.jpg', type: 'image', size: '2,1 Mo' }],
    timeline: [{ date: 'Aujourd’hui', label: 'Signalement reçu' }],
    messages: [
      {
        id: 'msg-2903-1',
        author: 'ville',
        authorName: 'Service animalier',
        sentAt: 'Aujourd’hui, 9 h 42',
        body: 'Merci pour votre signalement. Un patrouilleur animalier est assigné et vous contactera si l’animal est toujours sur place.',
      },
    ],
  },
  {
    id: 'req-2841',
    reference: '#REQ-2841',
    title: 'Nid-de-poule',
    service: 'Travaux publics',
    category: 'Voirie et chaussée',
    address: '4590, rue Bourgogne',
    fullAddress: '4590, rue Bourgogne, Brossard, Québec, J4W 2K1',
    submittedOn: 'Envoyée le 26 août',
    createdOn: '26/08/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'en-traitement',
    description:
      'Important nid-de-poule devant la propriété, dans la voie de droite. Le trou s’est élargi depuis les dernières pluies et plusieurs véhicules l’évitent en empiétant sur la voie opposée.',
    attachments: [
      { name: 'nid-de-poule-bourgogne.jpg', type: 'image', size: '1,8 Mo' },
      { name: 'vue-de-la-rue.jpg', type: 'image', size: '2,4 Mo' },
    ],
    timeline: [
      { date: '26 août', label: 'Demande reçue' },
      { date: '27 août', label: 'Transmise au service des travaux publics' },
      { date: '28 août', label: 'En traitement' },
      { date: '31 août', label: 'Inspection planifiée' },
    ],
    messages: [
      {
        id: 'msg-2841-1',
        author: 'ville',
        authorName: 'Travaux publics · Julie Béland',
        sentAt: '27 août, 10 h 15',
        body: 'Bonjour, votre signalement a été transmis à l’équipe de voirie. Une inspection est prévue cette semaine.',
      },
      {
        id: 'msg-2841-2',
        author: 'citoyen',
        authorName: 'Vous',
        sentAt: '28 août, 8 h 03',
        body: 'Merci. Le trou s’est encore agrandi depuis la pluie de dimanche, il fait maintenant environ 60 cm de large.',
      },
      {
        id: 'msg-2841-3',
        author: 'ville',
        authorName: 'Travaux publics · Julie Béland',
        sentAt: '29 août, 14 h 30',
        body: 'Merci pour la précision. Pouvez-vous confirmer si le nid-de-poule se trouve devant votre entrée ou plus près de l’intersection ? Une photo prise de loin nous aiderait à le localiser.',
        awaiting: 'info',
      },
      {
        id: 'msg-2841-4',
        author: 'ville',
        authorName: 'Travaux publics · Julie Béland',
        sentAt: '31 août, 9 h 05',
        body: 'Une réclamation pour dommage à un véhicule peut être ouverte si applicable. Veuillez remplir et nous retourner le formulaire ci-joint dans les 15 jours.',
        attachment: { name: 'formulaire-reclamation-dommages.pdf', type: 'form', size: '184 Ko' },
        awaiting: 'document',
      },
    ],
  },
  {
    id: 'req-2790',
    reference: '#REQ-2790',
    title: 'Lampadaire défectueux',
    service: 'Travaux publics',
    category: 'Éclairage public',
    address: '4610, rue Bourgogne',
    fullAddress: '4610, rue Bourgogne, Brossard, Québec, J4W 2K1',
    submittedOn: 'Envoyée le 12 août',
    createdOn: '12/08/26',
    resolvedOn: '18/08/26',
    createdBy: 'ATNA',
    status: 'resolue',
    description:
      'Le lampadaire devant le 4610 restait éteint la nuit, laissant le trottoir dans le noir entre les deux intersections.',
    attachments: [],
    timeline: [
      { date: '12 août', label: 'Demande reçue' },
      { date: '13 août', label: 'Transmise au service de l’éclairage' },
      { date: '18 août', label: 'Résolue' },
    ],
    messages: [
      {
        id: 'msg-2790-1',
        author: 'ville',
        authorName: 'Éclairage public',
        sentAt: '18 août, 16 h 20',
        body: 'L’ampoule a été remplacée aujourd’hui par l’équipe d’entretien. La requête est fermée, n’hésitez pas à nous écrire si le problème revient.',
      },
    ],
    response: 'L’ampoule a été remplacée le 18 août par l’équipe d’entretien.',
  },
]

export type PublicReportCategory = 'danger' | 'voirie' | 'eclairage' | 'arbre'

export type PublicReportStatus = 'nouveau' | 'en-traitement' | 'resolue'

export interface PublicReport {
  id: string
  category: PublicReportCategory
  title: string
  address: string
  distance: string
  reportedAgo: string
  status: PublicReportStatus
  confirmations: number
  lat: number
  lng: number
}

export const reportCategories: Record<PublicReportCategory, { label: string; color: string }> = {
  danger: { label: 'Danger', color: '#d93b30' },
  voirie: { label: 'Voirie', color: '#d97706' },
  eclairage: { label: 'Éclairage', color: '#8a6212' },
  arbre: { label: 'Arbres', color: '#37852f' },
}

export const publicReportStatusLabels: Record<PublicReportStatus, string> = {
  nouveau: 'Nouveau',
  'en-traitement': 'Équipe en route',
  resolue: 'Réglé',
}

/** Position approximative du domicile au profil (secteur B, Brossard). */
export const profileCoordinates: [number, number] = [45.4507, -73.4655]

export const publicReports: PublicReport[] = [
  {
    id: 'pub-1',
    category: 'voirie',
    title: 'Nid-de-poule profond',
    address: 'Boulevard Grande-Allée, près de la rue Bordeaux',
    distance: '300 m',
    reportedAgo: 'il y a 25 min',
    status: 'nouveau',
    confirmations: 7,
    lat: 45.4529,
    lng: -73.4622,
  },
  {
    id: 'pub-2',
    category: 'eclairage',
    title: 'Lampadaire éteint',
    address: '4610, rue Bourgogne',
    distance: '450 m',
    reportedAgo: 'il y a 2 h',
    status: 'en-traitement',
    confirmations: 12,
    lat: 45.4483,
    lng: -73.4693,
  },
  {
    id: 'pub-3',
    category: 'arbre',
    title: 'Branche tombée sur le trottoir',
    address: 'Parc Maupassant',
    distance: '650 m',
    reportedAgo: 'il y a 40 min',
    status: 'nouveau',
    confirmations: 4,
    lat: 45.4551,
    lng: -73.4701,
  },
  {
    id: 'pub-4',
    category: 'voirie',
    title: 'Affaissement de chaussée',
    address: 'Rue de Lyon, angle avenue Lakeshore',
    distance: '1,1 km',
    reportedAgo: 'il y a 5 h',
    status: 'en-traitement',
    confirmations: 18,
    lat: 45.4442,
    lng: -73.4581,
  },
  {
    id: 'pub-5',
    category: 'eclairage',
    title: 'Lampadaire qui clignote',
    address: 'Rue Alsace',
    distance: '800 m',
    reportedAgo: 'il y a 1 h',
    status: 'nouveau',
    confirmations: 9,
    lat: 45.4468,
    lng: -73.4738,
  },
  {
    id: 'pub-6',
    category: 'danger',
    title: 'Feu de circulation hors service',
    address: 'Boulevard Rome, angle boulevard Pelletier',
    distance: '1,4 km',
    reportedAgo: 'il y a 15 min',
    status: 'en-traitement',
    confirmations: 31,
    lat: 45.4573,
    lng: -73.4548,
  },
  {
    id: 'pub-7',
    category: 'voirie',
    title: 'Marquage de rue effacé',
    address: 'Avenue Panama',
    distance: '1,6 km',
    reportedAgo: 'hier',
    status: 'resolue',
    confirmations: 3,
    lat: 45.4415,
    lng: -73.4712,
  },
  {
    id: 'pub-8',
    category: 'arbre',
    title: 'Arbre à élaguer près des fils',
    address: 'Rue Chevrier',
    distance: '2,0 km',
    reportedAgo: 'il y a 3 h',
    status: 'nouveau',
    confirmations: 6,
    lat: 45.4602,
    lng: -73.4665,
  },
]

export const detailQuestions = [
  'Où en est ma demande ?',
  'Quand sera-t-elle traitée ?',
  'Est-ce que je dois faire autre chose ?',
]
